import type {
  ResidentDefaultFormValues,
  ResidentDetailResponse,
  ResidentDocumentGroup,
} from '@/features/resident/types'
import { lookupConstantLabel } from '@/features/resident/utils/residentListConstants'
import { RESIDENT_DEFAULT_FORM_VALUES } from '@/features/resident/constants/detailFormDefaults'
import {
  createEmptyReimbursementDocumentsMap,
  EMPTY_REIMBURSEMENT_EXTENDED_FIELDS,
  type ReimbursementDocumentTrigger,
  type ReimbursementExtendedFields,
  type ReimbursementWorkflowProgress,
} from '../types/reimbursementFormTypes'
import { syncApiDocumentsToLocalMap } from './reimbursementDocumentBridge'
import { reimbursementDocumentStatusToTrigger } from './reimbursementDocumentStatus'
import type { ReimbursementDetailResidentDefault } from '../types/domain'

export const REIMBURSEMENT_HEADER_LABELS = {
  company: 'Company',
  property: 'Property',
  propertySupportTeam: 'Property Support Team',
  lastUpdated: 'Last Updated',
  contractPurchaseDate: 'Contract Purchase Date',
  vintageYear: 'Vintage Year',
  contractStart: 'Contract Start',
  contractEnd: 'Contract End',
  leaseEndDate: 'Lease End Date',
  nrlsd: 'NRLSD',
  applicationApprover: 'Application Approver',
  requestDate: 'Request Date',
  startDate: 'Start Date',
  moveOutDate: 'Move Out Date',
  status: 'Status',
  progress: 'Progress',
  documents: 'Documents',
  review: 'Review Status',
} as const

export type ReimbursementWorkflowStatusPhase = 'open' | 'submitted' | 'concluded'

const CONCLUDED_CLAIM_STATUSES = new Set([
  'DENIED',
  'PAID',
  'CANCELLED',
  'EXPIRED',
  'CLAIM_DENIED',
])

const SUBMITTED_CLAIM_STATUSES = new Set([
  'UNDER_REVIEW',
  'LIBERTY_REVIEW',
  'EXTERNAL_REVIEW',
  'PAYMENT_PENDING',
  'RE_OPENED',
])

const OPEN_CLAIM_STATUSES = new Set(['OPEN', 'NOT_STARTED', 'IN_PROGRESS', ''])

const WORKFLOW_PROGRESS_LABELS: Record<ReimbursementWorkflowProgress, string> = {
  info_needed: 'Info Needed',
  action_needed: 'Action Needed',
  awaiting_final_info: 'Awaiting Final Info',
  ready_for_review: 'Ready for Review',
  in_review: 'In Review',
  denied: 'Denied',
  paid: 'Paid',
  expired: 'Expired',
  cancelled: 'Cancelled',
}

const WORKFLOW_PROGRESS_PILL_COLOR: Record<ReimbursementWorkflowProgress, string> = {
  info_needed: 'bg-yellow-100 text-yellow-800 border-yellow-300',
  action_needed: 'bg-orange-100 text-orange-800 border-orange-300',
  awaiting_final_info: 'bg-sky-100 text-sky-800 border-sky-300',
  ready_for_review: 'bg-green-100 text-green-800 border-green-300',
  in_review: 'bg-blue-100 text-blue-800 border-blue-300',
  denied: 'bg-red-100 text-red-800 border-red-300',
  paid: 'bg-emerald-100 text-emerald-800 border-emerald-300',
  expired: 'bg-gray-100 text-gray-600 border-gray-300',
  cancelled: 'bg-gray-100 text-gray-600 border-gray-300',
}

const WORKFLOW_PROGRESS_PILL_FALLBACK =
  'bg-slate-50 border-slate-200 text-slate-700'

const DOCUMENT_TRIGGER_LABELS: Record<ReimbursementDocumentTrigger, string> = {
  awaiting: 'Awaiting',
  uploaded: 'Uploaded',
  docs_approved: 'Docs Approved',
}

const DOCUMENT_TRIGGER_PILL_COLOR: Record<ReimbursementDocumentTrigger, string> = {
  docs_approved: 'bg-emerald-100 text-emerald-800 border-emerald-300',
  uploaded: 'bg-amber-100 text-amber-800 border-amber-300',
  awaiting: 'bg-gray-100 text-gray-600 border-gray-300',
}

const WORKFLOW_STATUS_DISPLAY: Record<ReimbursementWorkflowStatusPhase, string> = {
  open: 'Open',
  submitted: 'Submitted',
  concluded: 'Concluded',
}

const WORKFLOW_STATUS_PILL_COLOR: Record<ReimbursementWorkflowStatusPhase, string> = {
  open: 'bg-blue-100 text-blue-800 border-blue-300',
  submitted: 'bg-amber-100 text-amber-800 border-amber-300',
  concluded: 'bg-slate-100 text-slate-700 border-slate-300',
}

const WORKFLOW_PILL_BASE =
  'inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold'

function normalizeClaimStatusKey(claimStatus?: string | null): string {
  return String(claimStatus ?? '')
    .trim()
    .toUpperCase()
    .replace(/\s+/g, '_')
}

function normalizeReimbursmentStatusKey(claimStatus?: string | null): string {
  return String(claimStatus ?? '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '_')
}

export function resolveReimbursementWorkflowStatusPhase(
  claimStatus?: string | null,
): ReimbursementWorkflowStatusPhase {
  const key = normalizeClaimStatusKey(claimStatus)
  if (CONCLUDED_CLAIM_STATUSES.has(key)) return 'concluded'
  if (SUBMITTED_CLAIM_STATUSES.has(key)) return 'submitted'
  if (OPEN_CLAIM_STATUSES.has(key)) return 'open'
  return 'open'
}

export function resolveReimbursementStatusWorkflowPhase(
  reimbursement_rental_status?: string | null,
): ReimbursementWorkflowStatusPhase {
  return normalizeReimbursmentStatusKey(reimbursement_rental_status) as ReimbursementWorkflowStatusPhase
}

export function formatReimbursementWorkflowStatusLabel(
  claimStatus?: string | null,
): string {
  const phase = resolveReimbursementWorkflowStatusPhase(claimStatus)
  return WORKFLOW_STATUS_DISPLAY[phase]
}

/** Tailwind classes for the reimbursement card workflow status pill. */
export function getReimbursementWorkflowStatusPillClassName(
  reimbursement_rental_status?: string | null,
): string {
  const phase = resolveReimbursementStatusWorkflowPhase(reimbursement_rental_status)
  return `${WORKFLOW_PILL_BASE} ${WORKFLOW_STATUS_PILL_COLOR[phase]}`
}

/** Tailwind classes for the reimbursement card workflow progress pill. */
export function getReimbursementWorkflowProgressPillClassName(
  progress: ReimbursementWorkflowProgress | null | undefined,
): string {
  if (!progress) {
    return `${WORKFLOW_PILL_BASE} ${WORKFLOW_PROGRESS_PILL_FALLBACK}`
  }
  return `${WORKFLOW_PILL_BASE} ${WORKFLOW_PROGRESS_PILL_COLOR[progress]}`
}

export function formatWorkflowProgressLabel(
  progress: ReimbursementWorkflowProgress | null | undefined,
): string | null {
  if (!progress) return null
  return WORKFLOW_PROGRESS_LABELS[progress] ?? null
}

export function formatDocumentTriggerLabel(
  trigger: ReimbursementDocumentTrigger | null | undefined,
): string | null {
  if (!trigger) return null
  return DOCUMENT_TRIGGER_LABELS[trigger] ?? null
}

function normalizeRentalProgressKey(
  rentalProgress?: string | null,
): ReimbursementWorkflowProgress | null {
  const normalized = String(rentalProgress ?? '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '_')
  if (!normalized) return null
  if (normalized in WORKFLOW_PROGRESS_LABELS) {
    return normalized as ReimbursementWorkflowProgress
  }
  return null
}

export type ResolveHeaderProgressOptions = {
  rentalProgress?: string | null
  progressLabelMaps?: Record<string, string>
}

/** Header progress pill key from stored `reimbursement_rental_progress` only. */
export function resolveHeaderProgressKey(
  options: ResolveHeaderProgressOptions,
): ReimbursementWorkflowProgress | null {
  return normalizeRentalProgressKey(options.rentalProgress)
}

/** List/table progress pill — same normalization and colors as the reimbursement card header. */
export function getReimbursementListProgressPillClassName(
  rentalProgress?: string | null,
): string {
  const key = resolveHeaderProgressKey({ rentalProgress })
  return getReimbursementWorkflowProgressPillClassName(key)
}

/** List/table progress label — same resolution as the reimbursement card header. */
export function resolveReimbursementListProgressLabel(
  rentalProgress?: string | null,
  progressLabelMaps?: Record<string, string>,
): string | null {
  return resolveHeaderProgressLabel({ rentalProgress, progressLabelMaps })
}

export function mergeHeaderFormValues(
  residentDefault?: ReimbursementDetailResidentDefault | null,
  formOverlay?: Partial<ResidentDefaultFormValues> | null,
): ResidentDefaultFormValues {
  const rd = (residentDefault || {}) as Record<string, unknown>
  const base: ResidentDefaultFormValues = {
    ...RESIDENT_DEFAULT_FORM_VALUES,
    reimbursement_type: String(rd.reimbursement_type ?? ''),
    rental_status: String(rd.rental_status ?? ''),
    rental_progress: String(rd.rental_progress ?? ''),
    rental_status_date: String(rd.rental_status_date ?? ''),
    reason_for_default: String(rd.reason_for_default ?? ''),
    claim_status: String(rd.claim_status ?? ''),
    lease_agreement_status: String(rd.lease_agreement_status ?? ''),
    payment_ledger_status: String(rd.payment_ledger_status ?? ''),
    skip_claim_affidavit_status: String(rd.skip_claim_affidavit_status ?? ''),
    unlawful_detainer_status: String(rd.unlawful_detainer_status ?? ''),
    is_final_ledger: String(rd.is_final_ledger ?? ''),
    first_month_not_paid_month: String(rd.first_month_not_paid_month ?? ''),
    first_month_not_paid_year: String(rd.first_month_not_paid_year ?? ''),
    adjusted_notice: String(rd.adjusted_notice ?? ''),
    moveout_date: String(rd.moveout_date ?? ''),
    movein_date: String(rd.movein_date ?? ''),
    new_resident_lease_start_date: String(rd.new_resident_lease_start_date ?? ''),
    lease_end_date: String(rd.lease_end_date ?? ''),
    property_unused_security_deposit: String(rd.property_unused_security_deposit ?? rd.unused_security_deposit ?? ''),
    unused_security_deposit: String(rd.unused_security_deposit ?? ''),
    lease_agreement: String(rd.lease_agreement ?? ''),
    payment_ledger: String(rd.payment_ledger ?? ''),
    skip_claim_affidavit: String(rd.skip_claim_affidavit ?? ''),
    eviction_lawsuit: String(rd.eviction_lawsuit ?? rd.unlawful_detainer ?? ''),
  }

  if (!formOverlay) return base

  return {
    ...base,
    ...formOverlay,
    lease_end_date: formOverlay.lease_end_date ?? base.lease_end_date,
    rental_status_date: formOverlay.rental_status_date ?? base.rental_status_date,
    adjusted_notice: formOverlay.adjusted_notice ?? base.adjusted_notice,
    moveout_date: formOverlay.moveout_date ?? base.moveout_date,
  }
}

export function buildReimbursementHeaderLogicInput(options: {
  residentDefault?: ReimbursementDetailResidentDefault | null
  formOverlay?: Partial<ResidentDefaultFormValues> | null
  residentDocuments?: ResidentDocumentGroup | null
  extended?: ReimbursementExtendedFields
}) {
  const values = mergeHeaderFormValues(options.residentDefault, options.formOverlay)
  const documents = syncApiDocumentsToLocalMap(
    values,
    options.residentDocuments,
    createEmptyReimbursementDocumentsMap(),
  )
  return {
    values,
    extended: options.extended ?? EMPTY_REIMBURSEMENT_EXTENDED_FIELDS,
    documents,
  }
}

function documentTriggerFromApiStatus(raw: string): ReimbursementDocumentTrigger | null {
  return reimbursementDocumentStatusToTrigger(raw)
}

/** Header documents label from stored `document_status` only. */
export function resolveHeaderDocumentTriggerLabel(options: {
  documentStatus?: string | null
}): string {
  const trigger = documentTriggerFromApiStatus(String(options.documentStatus ?? ''))
  return formatDocumentTriggerLabel(trigger) ?? 'Awaiting'
}

/** Tailwind classes for the reimbursement card documents pill (from stored `document_status`). */
export function getReimbursementDocumentTriggerPillClassName(
  documentStatus?: string | null,
): string {
  const trigger = documentTriggerFromApiStatus(String(documentStatus ?? ''))
  return `${WORKFLOW_PILL_BASE} ${DOCUMENT_TRIGGER_PILL_COLOR[trigger ?? 'awaiting']}`
}

export function resolveHeaderProgressLabel(
  options: ResolveHeaderProgressOptions,
): string | null {
  const progressKey = resolveHeaderProgressKey(options)
  if (progressKey) {
    return formatWorkflowProgressLabel(progressKey)
  }

  const stored = lookupConstantLabel(
    options.progressLabelMaps,
    String(options.rentalProgress ?? ''),
  )
  return stored || null
}

export function resolveHeaderLastUpdatedValue(
  residentDefault?: ReimbursementDetailResidentDefault | null,
  applicationRecord?: Record<string, unknown> | null,
): unknown {
  const rd = residentDefault as { last_update_by_pm?: unknown; last_updated_date?: unknown } | null
  if (rd?.last_update_by_pm != null && String(rd.last_update_by_pm).trim() !== '') {
    return rd.last_update_by_pm
  }
  if (rd?.last_updated_date != null && String(rd.last_updated_date).trim() !== '') {
    return rd.last_updated_date
  }
  const app = applicationRecord ?? {}
  return app.lastUpdatedDate ?? app.last_updated_date ?? app.last_updated ?? ''
}

export function resolveContractPurchaseDate(
  application?: { contract_paid_date?: unknown } | null,
): unknown {
  return application?.contract_paid_date ?? ''
}

export type HeaderSummaryFormOverlay = {
  lease_end_date?: string
  rental_status_date?: string
  rental_status?: string
  adjusted_notice?: string
  moveout_date?: string
}

export function buildHeaderLogicFromDetail(
  residentDetail?: ResidentDetailResponse | null,
  formOverlay?: HeaderSummaryFormOverlay | null,
) {
  return buildReimbursementHeaderLogicInput({
    residentDefault: residentDetail?.resident_default,
    formOverlay,
    residentDocuments: residentDetail?.document,
  })
}
